/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { 
  RiUserLine, 
  RiProductHuntLine, 
  RiBuildingLine, 
  RiMailLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiAddLine,
  RiHomeLine,
  RiCalendarLine,
  RiMessageLine,
} from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductsContext';
import { useSponsors } from '../context/SponsorsContext';
import { useMessages } from '../context/MessagesContext';
import { useAppointments } from '../context/AppointmentsContext';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { products, loading: productsLoading } = useProducts();
  const { sponsors, loading: sponsorsLoading } = useSponsors();
  const { messages, stats: messageStats, loading: messagesLoading } = useMessages();
  const { appointments, stats: appointmentStats, loading: appointmentsLoading } = useAppointments();

  const [dashboardStats, setDashboardStats] = useState({
    totalProducts: 0,
    activeSponsors: 0,
    unreadMessages: 0,
    pendingAppointments: 0,
    productChange: '+0%',
    sponsorChange: '+0%',
    messageChange: '+0%',
    appointmentChange: '+0%'
  });

  useEffect(() => {
    const totalProducts = products?.length;
    const activeSponsors = sponsors?.filter(s => s.active).length;
    const unreadMessages = messages?.filter(m => !m.read).length;
    const pendingAppointments = appointments?.filter(a => a.status === 'pending').length;

    setDashboardStats({
      totalProducts,
      activeSponsors,
      unreadMessages,
      pendingAppointments,
      productChange: '+12%', 
      sponsorChange: '+5%',
      messageChange: '+23%',
      appointmentChange: '-3%'
    });
  }, [products, sponsors, messages, appointments]);

  const stats = [
    { 
      label: 'Total Products', 
      value: dashboardStats.totalProducts, 
      icon: <RiProductHuntLine />, 
      change: dashboardStats.productChange,
      color: 'blue' 
    },
    { 
      label: 'Active Sponsors', 
      value: dashboardStats.activeSponsors, 
      icon: <RiBuildingLine />, 
      change: dashboardStats.sponsorChange,
      color: 'green' 
    },
    { 
      label: 'Unread Messages', 
      value: dashboardStats.unreadMessages, 
      icon: <RiMailLine />, 
      change: dashboardStats.messageChange,
      color: 'purple' 
    },
    { 
      label: 'Pending Appointments', 
      value: dashboardStats.pendingAppointments, 
      icon: <RiCalendarLine />, 
      change: dashboardStats.appointmentChange,
      color: 'orange' 
    }
  ];

  const recentActivities = [
    { id: 1, action: 'New product added', user: 'Admin', time: '2 hours ago', type: 'product' },
    { id: 2, action: 'Sponsor updated', user: 'Admin', time: '4 hours ago', type: 'sponsor' },
    { id: 3, action: 'Appointment booked', user: 'Gatwich', time: '1 day ago', type: 'appointment' },
    { id: 4, action: 'Contact form submitted', user: 'Staff', time: '2 days ago', type: 'message' }
  ];

  const quickActions = [
    { label: 'Add New Product', icon: <RiAddLine />, path: '/admin/products', color: 'blue' },
    { label: 'Manage Sponsors', icon: <RiBuildingLine />, path: '/admin/sponsors', color: 'green' },
    { label: 'View Messages', icon: <RiMessageLine />, path: '/admin/messages', color: 'purple' },
    { label: 'Update Homepage', icon: <RiHomeLine />, path: '/admin/content', color: 'orange' },
  ];

  if (productsLoading || sponsorsLoading || messagesLoading || appointmentsLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard Overview</h1>
          <p>Welcome back, {currentUser?.name || 'Admin'}! Here's what's happening with your website.</p>
        </div>
        <div className="header-actions">
          <div className="user-info">
            <RiUserLine className="user-icon" />
            <div>
              <span className="user-name">{currentUser?.name || 'Admin User'}</span>
              <span className="user-role">{currentUser?.role || 'Administrator'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        {stats?.map((stat, index) => (
          <div key={index} className={`stat-card stat-${stat.color}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <h3>{stat.value?.toLocaleString()}</h3>
              <span className="stat-label">{stat.label}</span>
            </div>
            <div className={`stat-change ${stat.change?.startsWith('+') ? 'positive' : 'negative'}`}>
              {stat.change?.startsWith('+') ? <RiArrowUpLine /> : <RiArrowDownLine />}
              <span>{stat.change}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-content">
        <div className="dashboard-column left-column">
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Recent Activities</h2>
              <a href="/admin/activities" className="view-all">View All</a>
            </div>
            <div className="activities-list">
              {recentActivities.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className={`activity-icon activity-${activity.type}`}>
                    {activity.type === 'product' && <RiProductHuntLine />}
                    {activity.type === 'sponsor' && <RiBuildingLine />}
                    {activity.type === 'appointment' && <RiCalendarLine />}
                    {activity.type === 'message' && <RiMessageLine />}
                  </div>
                  <div className="activity-content">
                    <h4>{activity.action}</h4>
                    <p>By {activity.user}</p>
                  </div>
                  <span className="activity-time">{activity.time}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h2>Latest Messages</h2>
              <a href="/admin/messages" className="view-all">View All</a>
            </div>
            <div className="messages-list">
              {messages?.slice(0, 5).map(message => (
                <div key={message.id} className="message-item">
                  <div className="message-header">
                    <span className="message-sender">{message.name}</span>
                    <span className={`message-status ${message.read ? 'read' : 'unread'}`}>
                      {message.read ? 'Read' : 'Unread'}
                    </span>
                  </div>
                  <p className="message-preview">{message.subject}</p>
                  <span className="message-time">
                    {new Date(message.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dashboard-column right-column">
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="quick-actions-grid">
              {quickActions.map((action, index) => (
                <a key={index} href={action.path} className={`quick-action action-${action.color}`}>
                  <div className="action-icon">{action.icon}</div>
                  <span className="action-label">{action.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h2>Today's Messages</h2>
              <a href="/admin/messages" className="view-all">View All</a>
            </div>
            <div className="appointments-list">
              {messages?.slice(0, 4).map(message => (
                <div key={message.id} className="appointment-item">
                  <div className="appointment-time">
                    <span className="time">{message.time}</span>
                    <span className="date">{new Date(message.date).toLocaleDateString()}</span>
                  </div>
                  <div className="appointment-details">
                    <h4>{message.name}</h4>
                    <p>{message.service}</p>
                    <span className={`appointment-status status-${message.status}`}>
                      {message.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;